import { useEffect, useMemo, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useCreateComponentMutation } from '../../api/authoring'
import type { Document, Manifest } from '../../api/authoring'
import { problemMessage } from '../../api/baseApi'
import { SchemaForm } from '../../editors/SchemaForm'
import { creationFields, creationSchema } from '../../model/create'
import { humanise } from '../../model/labels'
import { fieldsSchema, typeProse } from '../../model/manifest'
import { keyAndLabel, ownedReferences, recordPath, slugify, type RecordAddress } from '../../model/records'
import { shown } from '../../api/useIntent'

/**
 * A new record, as an owner makes one: its words, and nothing about the
 * model.
 *
 * What the record owns (an album's Gallery) and the list it joins are the
 * server's to create, so this sends the fields alone and says what will
 * come with them. The key follows the title until the owner edits it,
 * because a slug is something an owner accepts rather than invents, and
 * the address it makes is shown as it is typed.
 */
export function NewRecordDialog({
  tenant,
  typeName,
  manifest,
  address,
  open,
  onClose,
  onCreated,
}: {
  tenant: string
  typeName: string
  manifest: Manifest | undefined
  address: RecordAddress | undefined
  open: boolean
  onClose: () => void
  onCreated: (document: Document) => void
}) {
  const [create, { isLoading, error, reset }] = useCreateComponentMutation()
  const [fields, setFields] = useState<Record<string, unknown>>({})
  // Whether the owner has written the key themselves, after which the
  // title stops rewriting it.
  const [keyEdited, setKeyEdited] = useState(false)
  const schema = useMemo(() => creationSchema(manifest, typeName), [manifest, typeName])
  const root = useMemo(() => fieldsSchema(manifest, typeName), [manifest, typeName])
  const { key, label } = keyAndLabel(manifest, typeName)
  const prose = typeProse(manifest, typeName)
  const owned = ownedReferences(manifest, typeName)

  // Cleared when the dialog opens, and only then: `reset` changes when a
  // request starts, so as a dependency it emptied the form on Create and
  // a refusal had nothing left to explain. See AddPageDialog.
  const resetMutation = useRef(reset)
  resetMutation.current = reset
  useEffect(() => {
    if (!open) return
    setFields({})
    setKeyEdited(false)
    resetMutation.current()
  }, [open])

  const change = (next: Record<string, unknown>) => {
    if (key && next[key] !== fields[key]) setKeyEdited(true)
    if (key && label && !keyEdited && next[label] !== fields[label] && next[key] === fields[key]) {
      next = { ...next, [key]: slugify(String(next[label] ?? '')) }
    }
    setFields(next)
  }

  const submit = async () => {
    const created = await create({
      tenant,
      createComponent: { type: typeName, fields: creationFields(fields) },
    }).unwrap()
    onCreated(created)
  }

  const path = recordPath(address, key ? (fields[key] as string | undefined) : undefined)

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New {prose.title.toLowerCase()}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {schema && root && <SchemaForm schema={schema} root={root} value={fields} onChange={change} disabled={isLoading} />}
          {path && (
            <Typography variant="body2" color="text.secondary">
              Its page will be at <strong>{path}</strong> once the site is published.
            </Typography>
          )}
          {owned.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {`It comes with its own ${owned.map((reference) => humanise(reference.name).toLowerCase()).join(' and ')}, ready to fill in on the next screen.`}
            </Typography>
          )}
          {error && <Alert severity="error">{problemMessage(error, `The ${prose.title.toLowerCase()} was not made.`)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => submit().catch(shown)} disabled={isLoading}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  )
}
